import React from 'react';
import { Gamepad2, ListOrdered, MapPin, Route, Settings, type LucideIcon } from 'lucide-react';
import {
  formatCoins,
  type CoinModule,
  type CoinModuleSlice,
  type CoinPoint,
} from '../../data/coin-usage-mock';

const MODULE_ICON: Record<CoinModule, LucideIcon> = {
  stationary: MapPin,
  playlist: ListOrdered,
  itinerary: Route,
  joystick: Gamepad2,
  other: Settings,
};

function axisTop(max: number): number {
  const steps = [10, 20, 40, 80, 100, 200, 400, 800, 1000];
  return steps.find((step) => step >= max) ?? 1000;
}

export function UsageChart({
  points,
  activeHint,
  onPick,
  label,
}: {
  points: CoinPoint[];
  activeHint: string;
  onPick: (hint: string) => void;
  label: string;
}) {
  const peak = points.reduce((max, point) => Math.max(max, point.coins), 0);
  const top = axisTop(peak);
  const ticks = [top, top * 0.75, top * 0.5, top * 0.25, 0];

  return (
    <div className="cu-chart">
      <div className="cu-chart__y" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
      <div className="cu-chart__scroll">
        <div className="cu-plot">
          <div className="cu-bars" role="group" aria-label={label}>
            <span className="cu-grid" style={{ bottom: '100%' }} />
            <span className="cu-grid" style={{ bottom: '75%' }} />
            <span className="cu-grid" style={{ bottom: '50%' }} />
            <span className="cu-grid" style={{ bottom: '25%' }} />
            <span className="cu-grid" style={{ bottom: '0%' }} />
            {points.map((point) => {
              const active = point.hint === activeHint;
              const height = top === 0 ? 0 : (point.coins / top) * 100;
              return (
                <button
                  key={point.hint}
                  type="button"
                  className={active ? 'cu-bar is-active' : 'cu-bar'}
                  style={{ ['--h' as string]: `${height}%` }}
                  aria-pressed={active}
                  aria-label={`${point.hint}, ${point.coins} coins`}
                  onClick={() => onPick(point.hint)}
                >
                  <span className="cu-tip">
                    <strong>{point.coins} coins</strong>
                    <span>{point.hint}</span>
                  </span>
                  <span className="cu-bar__fill" />
                </button>
              );
            })}
          </div>
          <div className="cu-x" aria-hidden="true">
            {points.map((point) => (
              <span key={point.hint}>{point.showLabel ? point.label : ''}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ModuleDonut({ total, slices }: { total: number; slices: CoinModuleSlice[] }) {
  const size = 168;
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const gap = slices.length > 1 ? 5 : 0;
  let cursor = 0;

  return (
    <div className="cu-module">
      <div className="cu-donut">
        <svg className="cu-donut__svg" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <g transform={`rotate(-90 ${center} ${center})`}>
            {slices.map((slice) => {
              const portion = (slice.pct / 100) * circumference;
              const dash = Math.max(0, portion - gap);
              const offset = cursor;
              cursor += portion;
              return (
                <circle
                  key={slice.id}
                  className={`cu-donut__arc cu-donut__arc--${slice.id}`}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  strokeWidth={stroke}
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={-offset}
                />
              );
            })}
          </g>
        </svg>
        <div className="cu-donut__center">
          <strong>{formatCoins(total)}</strong>
          <span>Coins</span>
        </div>
      </div>
      <ul className="cu-legend">
        {slices.map((slice) => {
          const Icon = MODULE_ICON[slice.id];
          return (
            <li key={slice.id}>
              <span className={`cu-bubble cu-bubble--${slice.id}`}>
                <Icon size={15} aria-hidden="true" />
              </span>
              <span className="cu-legend__name">{slice.label}</span>
              <span className="cu-legend__coins">{formatCoins(slice.coins)} coins</span>
              <span className="cu-legend__pct">{slice.pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function moduleIcon(module: CoinModule): LucideIcon {
  return MODULE_ICON[module];
}
